import { getDb } from './db'
import type {
  AcceptanceRow,
  AcceptanceWithVersion,
  AgreementWithVersion,
  DeliverableVersionPublicRow,
  EntitlementStatus,
  EntitlementWithProduct,
} from './types'

/**
 * Phase-2 flow logic shared by the Agreements and Downloads pages.
 *
 * The pure helpers mirror (never replace) the server verdict in
 * apps/portal/functions/api/_lib/gate.ts — they only derive UI state; the
 * download gate re-checks everything server-side before any bytes move.
 */

/** Base URL of the Pages Functions API ('' = same origin, the production case). */
export function apiBase(): string {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? ''
  return base.replace(/\/+$/, '')
}

// ─────────────────────────────────────────────────────────────────────────
// Pure state derivation
// ─────────────────────────────────────────────────────────────────────────

export type DownloadPhase = 'locked' | 'reviewing' | 'ready' | 'downloading' | 'done'

/**
 * Derive the download button state from entitlement + acceptance facts.
 * `downloading`/`done` are runtime states the component owns; this returns
 * the resting state: locked (no usable entitlement) → reviewing (acceptance
 * needed) → ready.
 */
export function deriveDownloadPhase(input: {
  entitlementStatus: EntitlementStatus
  entitlementExpiresAt: string | null
  /** Id of the CURRENT version of the required document family. */
  currentDocumentVersionId: string | null
  /** The user's most relevant acceptance for that document family. */
  acceptedDocumentVersionId: string | null
  now?: Date
}): 'locked' | 'reviewing' | 'ready' {
  const now = input.now ?? new Date()
  if (input.entitlementStatus !== 'active') return 'locked'
  if (
    input.entitlementExpiresAt !== null &&
    new Date(input.entitlementExpiresAt).getTime() <= now.getTime()
  ) {
    return 'locked'
  }
  if (input.currentDocumentVersionId === null) return 'reviewing'
  if (input.acceptedDocumentVersionId !== input.currentDocumentVersionId) return 'reviewing'
  return 'ready'
}

export interface AgreementStatus {
  chip: 'accepted' | 'action-required'
  acceptedAt: string | null
  /** Accepted an older version than the current one (acceptance_stale). */
  stale: boolean
  acceptedDocumentVersionId: string | null
}

/** Chip state for one agreement row. */
export function deriveAgreementStatus(input: {
  currentVersionId: string
  acceptances: Array<{ document_version_id: string; accepted_at: string }>
}): AgreementStatus {
  const latest = input.acceptances[0] ?? null // callers sort newest-first
  if (!latest) {
    return { chip: 'action-required', acceptedAt: null, stale: false, acceptedDocumentVersionId: null }
  }
  const acceptedCurrent = latest.document_version_id === input.currentVersionId
  return {
    chip: acceptedCurrent ? 'accepted' : 'action-required',
    acceptedAt: latest.accepted_at,
    stale: !acceptedCurrent,
    acceptedDocumentVersionId: latest.document_version_id,
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Downloads page view model (pure)
// ─────────────────────────────────────────────────────────────────────────

export interface DownloadCardModel {
  /** Stable key: entitlement id + deliverable version id. */
  key: string
  versionId: string | null
  title: string
  subtitle: string
  entitlementStatus: EntitlementStatus
  entitlementExpiresAt: string | null
  restingPhase: 'locked' | 'reviewing' | 'ready'
  statusTone: 'ready' | 'awaiting' | 'expired'
  statusLabel: string
  notice: string | null
  /** The document that gates this download (for the Review & Accept modal). */
  requiredDocument: {
    currentVersionId: string
    title: string
    version: string
    summary: string | null
    rendered_text: string | null
  } | null
}

/**
 * Compose the Downloads page cards from raw rows. Pure so the entitlement /
 * acceptance / staleness rules are unit-testable and identical to the server
 * verdict (the gate re-checks everything regardless).
 */
export function buildDownloadCards(input: {
  entitlements: EntitlementWithProduct[]
  versions: DeliverableVersionPublicRow[]
  agreements: AgreementWithVersion[]
  acceptances: AcceptanceWithVersion[]
  now?: Date
}): DownloadCardModel[] {
  const now = input.now ?? new Date()
  const cards: DownloadCardModel[] = []

  for (const entitlement of input.entitlements) {
    const productName = entitlement.products?.name ?? 'Untitled product'
    const orgName = entitlement.organizations?.name
    const subtitleBase = orgName ? `${productName} · ${orgName}` : productName
    const productVersions = entitlement.products
      ? input.versions.filter((v) => v.product_id === entitlement.products?.id)
      : []

    if (productVersions.length === 0) {
      cards.push({
        key: `${entitlement.id}:none`,
        versionId: null,
        title: productName,
        subtitle: subtitleBase,
        entitlementStatus: entitlement.status,
        entitlementExpiresAt: entitlement.expires_at,
        restingPhase: 'locked',
        statusTone: 'expired',
        statusLabel: 'No files',
        notice: 'No downloadable files have been published for this product yet.',
        requiredDocument: null,
      })
      continue
    }

    for (const version of productVersions) {
      const agreement = input.agreements.find((a) => a.doc_type === version.required_document_type)
      const currentVersion = agreement?.document_versions[0] ?? null
      const currentVersionId = currentVersion?.id ?? null
      const docAcceptances = currentVersion
        ? input.acceptances.filter((a) => a.document_versions?.document_id === agreement?.id)
        : []
      const acceptedId =
        docAcceptances.find((a) => a.document_version_id === currentVersionId)
          ?.document_version_id ??
        docAcceptances[0]?.document_version_id ??
        null

      const restingPhase = deriveDownloadPhase({
        entitlementStatus: entitlement.status,
        entitlementExpiresAt: entitlement.expires_at,
        currentDocumentVersionId: currentVersionId,
        acceptedDocumentVersionId: acceptedId,
        now,
      })

      const expired =
        entitlement.status === 'active' &&
        entitlement.expires_at !== null &&
        new Date(entitlement.expires_at).getTime() <= now.getTime()

      const notice =
        restingPhase === 'ready'
          ? null
          : restingPhase === 'locked'
            ? expired
              ? `Your entitlement expired on ${new Date(entitlement.expires_at ?? '').toLocaleDateString()}.`
              : `Your entitlement for this product is ${entitlement.status}.`
            : currentVersionId === null
              ? 'The required license document is not available yet.'
              : `Accept the ${agreement?.title ?? 'Delivery License'} to unlock this download.`

      cards.push({
        key: `${entitlement.id}:${version.id}`,
        versionId: version.id,
        title: version.deliverable_name,
        subtitle: `${subtitleBase} · v${version.version}`,
        entitlementStatus: entitlement.status,
        entitlementExpiresAt: entitlement.expires_at,
        restingPhase,
        statusTone:
          restingPhase === 'ready' ? 'ready' : restingPhase === 'reviewing' ? 'awaiting' : 'expired',
        statusLabel:
          restingPhase === 'ready' ? 'Ready' : restingPhase === 'reviewing' ? 'Action required' : 'Locked',
        notice,
        requiredDocument:
          agreement && currentVersion
            ? {
                currentVersionId: currentVersion.id,
                title: agreement.title,
                version: currentVersion.version,
                summary: currentVersion.summary,
                rendered_text: currentVersion.rendered_text,
              }
            : null,
      })
    }
  }

  return cards
}

// ─────────────────────────────────────────────────────────────────────────
// Gate + acceptance I/O
// ─────────────────────────────────────────────────────────────────────────

export type DownloadGateFailureReason =
  | 'no_session'
  | 'no_entitlement'
  | 'acceptance_required'
  | 'acceptance_stale'
  | 'version_not_found'
  | 'upstream_error'
  | 'audit_write_failed'
  | 'unknown'

export type DownloadGateResult =
  | { ok: true; url: string; expiresAt: string }
  | { ok: false; reason: DownloadGateFailureReason }

/**
 * Call the server-side download gate and classify the outcome. The signed URL
 * is returned to the caller for navigation — the client cannot mint one.
 */
export async function requestDownload(
  versionId: string,
  accessToken: string,
): Promise<DownloadGateResult> {
  let res: Response
  try {
    res = await fetch(`${apiBase()}/api/download/${encodeURIComponent(versionId)}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  } catch {
    return { ok: false, reason: 'unknown' }
  }

  if (res.ok) {
    const body = (await res.json()) as { url?: string; expires_at?: string }
    if (typeof body.url !== 'string') return { ok: false, reason: 'unknown' }
    return {
      ok: true,
      url: body.url,
      expiresAt: body.expires_at ?? new Date().toISOString(),
    }
  }

  const body = (await res.json().catch(() => null)) as { reason?: string } | null
  const reason = body?.reason
  if (
    reason === 'no_session' ||
    reason === 'no_entitlement' ||
    reason === 'acceptance_required' ||
    reason === 'acceptance_stale' ||
    reason === 'version_not_found' ||
    reason === 'upstream_error' ||
    reason === 'audit_write_failed'
  ) {
    return { ok: false, reason }
  }
  return { ok: false, reason: 'unknown' }
}

/** Best-effort public IP via the whoami helper (never throws). */
async function fetchClientIp(): Promise<string | null> {
  try {
    const res = await fetch(`${apiBase()}/api/whoami`)
    if (!res.ok) return null
    const body = (await res.json()) as { ip?: unknown }
    return typeof body.ip === 'string' ? body.ip : null
  } catch {
    return null
  }
}

/**
 * Record one acceptance in the immutable ledger (client INSERT via RLS
 * insert-only policy). The click IS the assent — no checkbox. The database
 * stamps accepted_at (server time); ip/user_agent are recorded with the row.
 */
export async function recordAcceptance(input: {
  userId: string
  documentVersionId: string
  orderId?: string | null
}): Promise<AcceptanceRow> {
  const ip = await fetchClientIp()
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null
  return getDb().insertAcceptance({
    userId: input.userId,
    documentVersionId: input.documentVersionId,
    ip,
    userAgent,
    orderId: input.orderId ?? null,
  })
}
